import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../../../core/store/store';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import userService, { UserWithRole, RoleHistory } from '../../../../shared/services/userService';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import UserFilters from './UserFilters';
import UserTable from './UserTable';
import ViewInfoBanner from './ViewInfoBanner';
import Pagination from './Pagination';
import RoleChangeModal from './modals/RoleChangeModal';
import RoleHistoryModal from './modals/RoleHistoryModal';
import PanelAccessModal from './modals/PanelAccessModal';
import ReferentsModal from './modals/ReferentsModal';
import SanctionModal from './modals/SanctionModal';
import UserDetailsModal from './modals/UserDetailsModal';
import { useUserManagement } from '../hooks/useUserManagement';
import { useReferentsManagement } from '../hooks/useReferentsManagement';
import { usePanelAccessManagement } from '../hooks/usePanelAccessManagement';

/**
 * Contenu « Gérer les rôles » réutilisable (sans layout).
 * Utilisé dans UserRolesManagement et dans CmManagement (onglet Référent CM).
 */
type UserRolesManagementContentProps = {
  /**
   * Type d'utilisateurs à afficher par défaut dans la section "Gérer les rôles".
   * Utile pour l'onglet "Gestion des référents" (on se concentre sur l'équipe).
   */
  defaultUserType?: 'all' | 'team' | 'players';
  mode?: 'users' | 'moderation';
};

const UserRolesManagementContent: React.FC<UserRolesManagementContentProps> = ({
  defaultUserType = 'all',
  mode = 'users',
}) => {
  const isModerationMode = mode === 'moderation';
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const { t } = usePageTranslation('panel', 'UserRolesManagement');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<number | ''>('');
  const [userType, setUserType] = useState<'all' | 'team' | 'players'>(defaultUserType);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPanelAccessModal, setShowPanelAccessModal] = useState(false);
  const [showReferentsModal, setShowReferentsModal] = useState(false);
  const [showSanctionModal, setShowSanctionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<string | null>(null);
  const [roleHistory, setRoleHistory] = useState<RoleHistory[]>([]);
  const [newRoleId, setNewRoleId] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());

  const { users, roles, loading, error, totalPages, usersReferents, setError, reload } = useUserManagement({
    searchTerm,
    selectedRole,
    page,
  });

  const referentsManagement = useReferentsManagement();
  const panelAccessManagement = usePanelAccessManagement();

  const handleRoleChange = async () => {
    if (!selectedUser || !newRoleId) return;
    try {
      setUpdating(true);
      const response = await userService.updateUserRole(selectedUser.id, newRoleId, reason || undefined);
      if (response.success) {
        setShowRoleModal(false);
        setReason('');
        await reload();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour du rôle');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenSanctionModal = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowSanctionModal(true);
  };

  const handleSanction = async (type: 'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute', duration?: number, reason?: string) => {
    if (!selectedUser) return;
    try {
      setUpdating(true);
      const response = await userService.updateUserAccess(selectedUser.id, type === 'unban' || type === 'unmute', reason);
      if (response.success) {
        setShowSanctionModal(false);
        await reload();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'application de la sanction');
    } finally {
      setUpdating(false);
    }
  };

  const handleViewHistory = async (user: UserWithRole) => {
    try {
      const response = await userService.getUserRoleHistory(user.id);
      if (response.success) {
        setRoleHistory(response.data);
        setSelectedUser(user);
        setShowHistoryModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de l\'historique');
    }
  };

  const handleOpenPanelAccess = async (user: UserWithRole) => {
    try {
      const success = await panelAccessManagement.loadPanelAccess(user);
      if (success) {
        setSelectedUser(user);
        setShowPanelAccessModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des accès panel');
    }
  };

  const handleTogglePanelAccess = async (section: string, hasAccess: boolean) => {
    if (!selectedUser) return;
    try {
      await panelAccessManagement.toggleAccess(selectedUser.id, section, hasAccess, currentUser?.username || 'Admin');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour de l\'accès panel');
    }
  };

  const handleOpenReferents = async (user: UserWithRole) => {
    if (!currentUser || currentUser.roles_id !== 1) {
      setError('Seul le Founder peut gérer les référents.');
      return;
    }
    if (user.roles_id < 1 || user.roles_id > 16) {
      setError('Cet utilisateur ne peut pas être référent. Seuls les membres de l\'équipe (rôles 1 à 16) peuvent être référents.');
      return;
    }
    try {
      const success = await referentsManagement.loadReferents(user);
      if (success) {
        setSelectedUser(user);
        setShowReferentsModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des référents');
    }
  };

  const handleAssignReferent = async () => {
    if (!selectedUser) return;
    try {
      await referentsManagement.assignReferent(selectedUser.id, () => reload());
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'assignation du référent');
    }
  };

  const handleRemoveReferent = async (referentFunction: string) => {
    if (!selectedUser) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer la fonction "${referentFunction}" de ${selectedUser.username} ?`)) return;
    try {
      await referentsManagement.removeReferent(selectedUser.id, referentFunction);
      await reload();
    } catch (err: any) {
      setError(err.message || 'Erreur lors du retrait du référent');
    }
  };

  const isTeamMember = (user: UserWithRole): boolean => user.roles_id >= 1 && user.roles_id <= 16;

  const filteredUsers = users.filter(user => {
    if (userType === 'team') return isTeamMember(user);
    if (userType === 'players') return user.roles_id === 18;
    return true;
  });

  const openRoleModal = (user: UserWithRole) => {
    setSelectedUser(user);
    setNewRoleId(user.roles_id);
    setReason('');
    setShowRoleModal(true);
  };

  const handleViewDetails = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setResetPasswordMessage(null);
  };

  const handleResetPassword = async (userId: number) => {
    try {
      setResettingPassword(true);
      setResetPasswordMessage(null);
      const response = await userService.resetUserPassword(userId);
      setResetPasswordMessage(response.success ? (response.message || 'Email de réinitialisation envoyé avec succès') : (response.error || 'Erreur'));
    } catch (err: any) {
      setResetPasswordMessage(err.message || 'Erreur lors de l\'envoi de l\'email de réinitialisation');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleForceLogout = async (userId: number) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    if (!window.confirm(`Déconnecter de force ${user.username} ?`)) return;
    try {
      setUpdating(true);
      const response = await userService.forceLogoutUser(userId);
      if (response.success) {
        setOnlineUserIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la déconnexion forcée');
    } finally {
      setUpdating(false);
    }
  };

  React.useEffect(() => {
    if (!isModerationMode) return;
    const loadOnlineUsers = async () => {
      try {
        const response = await userService.getOnlineUsers();
        if (response.success && response.data) {
          setOnlineUserIds(new Set(response.data.map((u) => u.user_id)));
        }
      } catch {
        // ignore
      }
    };
    void loadOnlineUsers();
    const timer = window.setInterval(() => {
      void loadOnlineUsers();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isModerationMode]);

  return (
    <>
      <UserFilters
        searchTerm={searchTerm}
        selectedRole={selectedRole}
        roles={roles}
        onSearchChange={(value) => { setSearchTerm(value); setPage(1); }}
        onRoleChange={(value) => { setSelectedRole(value); setPage(1); }}
      />

      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => setUserType('all')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'all' ? 'border-purple-500/40 bg-purple-500/20 text-purple-200' : 'border-[#2b3a61] bg-[#0d1b3a]/80 text-slate-300 hover:border-purple-500/30 hover:text-white'}`}
        >
          {t('filters.allUsers')}
        </button>
        <button
          onClick={() => setUserType('team')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'team' ? 'border-[#3d4fdb]/40 bg-[#3d4fdb]/20 text-[#8ea0f5]' : 'border-[#2b3a61] bg-[#0d1b3a]/80 text-slate-300 hover:border-[#3d4fdb]/30 hover:text-white'}`}
        >
          {t('filters.team')}
        </button>
        <button
          onClick={() => setUserType('players')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'players' ? 'border-[#3d4fdb]/40 bg-[#3d4fdb]/20 text-[#8ea0f5]' : 'border-[#2b3a61] bg-[#0d1b3a]/80 text-slate-300 hover:border-[#3d4fdb]/30 hover:text-white'}`}
        >
          {t('filters.players')}
        </button>
      </div>

      <ViewInfoBanner viewParam={viewParam} />

      {error && (
        <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <UserTable
            users={filteredUsers}
            onRoleChange={openRoleModal}
            onAccessToggle={handleOpenSanctionModal}
            onViewHistory={handleViewHistory}
            onPanelAccess={handleOpenPanelAccess}
            onReferents={handleOpenReferents}
            onViewDetails={handleViewDetails}
            onForceLogout={isModerationMode ? handleForceLogout : undefined}
            currentUserId={currentUser?.id}
            currentUserRoleId={currentUser?.roles_id}
            updating={updating}
            isTeamMember={isTeamMember}
            usersReferents={usersReferents}
            onlineUserIds={isModerationMode ? onlineUserIds : undefined}
            readOnly={isModerationMode}
          />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {showRoleModal && selectedUser && (
        <RoleChangeModal
          user={selectedUser}
          roles={roles}
          newRoleId={newRoleId}
          reason={reason}
          updating={updating}
          onRoleIdChange={setNewRoleId}
          onReasonChange={setReason}
          onConfirm={handleRoleChange}
          onCancel={() => { setShowRoleModal(false); setReason(''); }}
        />
      )}

      {showHistoryModal && selectedUser && (
        <RoleHistoryModal
          user={selectedUser}
          history={roleHistory}
          onClose={() => { setShowHistoryModal(false); setRoleHistory([]); }}
        />
      )}

      {showPanelAccessModal && selectedUser && (
        <PanelAccessModal
          user={selectedUser}
          panelAccess={panelAccessManagement.panelAccess}
          roleDefaultAccess={panelAccessManagement.roleDefaultAccess}
          panelSections={panelAccessManagement.panelSections}
          updating={panelAccessManagement.updating}
          onToggleAccess={handleTogglePanelAccess}
          onClose={() => { setShowPanelAccessModal(false); panelAccessManagement.reset(); }}
        />
      )}

      {showReferentsModal && selectedUser && (
        <ReferentsModal
          user={selectedUser}
          referents={referentsManagement.referents}
          referentFunctions={referentsManagement.referentFunctions}
          newReferentFunction={referentsManagement.newReferentFunction}
          newReferentDescription={referentsManagement.newReferentDescription}
          updating={referentsManagement.updating}
          onFunctionChange={referentsManagement.setNewReferentFunction}
          onDescriptionChange={referentsManagement.setNewReferentDescription}
          onAssign={handleAssignReferent}
          onRemove={handleRemoveReferent}
          onClose={() => { setShowReferentsModal(false); referentsManagement.reset(); }}
        />
      )}

      {showSanctionModal && selectedUser && (
        <SanctionModal
          user={selectedUser}
          updating={updating}
          onSanction={handleSanction}
          onCancel={() => { setShowSanctionModal(false); setSelectedUser(null); }}
          currentUserRoleId={currentUser?.roles_id}
          context="roles"
        />
      )}

      {showDetailsModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => { setShowDetailsModal(false); setSelectedUser(null); setResetPasswordMessage(null); }}
          onResetPassword={handleResetPassword}
          resettingPassword={resettingPassword}
          onSanction={(userId) => { setSelectedUser(users.find(u => u.id === userId) || null); setShowSanctionModal(true); }}
          currentUserRoleId={currentUser?.roles_id}
          context="roles"
        />
      )}

      {resetPasswordMessage && (
        <div className={`mt-6 rounded-lg px-3 py-2 text-sm border ${
          resetPasswordMessage.includes('succès') || resetPasswordMessage.includes('envoyé')
            ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
            : 'bg-red-500/10 border-red-500/40 text-red-200'
        }`}>
          {resetPasswordMessage}
        </div>
      )}
    </>
  );
};

export default UserRolesManagementContent;
